import React, { useState, useEffect } from "react";
import Search from "../Search/Search";
import ShipmentTable from "../shipmentTable/ShipmentTable";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import moment from "moment";
import { Box } from "@mui/material";
import { fetchShipments } from "../../api/shipmentTable";
import { fetchMetrics } from "../../api/metrics";
import AddCustomerPopup from "../../components/AddCustomerPopup";
import CustomerShipmentTable from "../CustomerShipmentTable/CustomerShipmentTable";
import {
	convertUTCTimeToLocalTime,
	roundToTwoDecimalPlaces,
	formatDeliveryStatus,
} from "../../utilities/shipmentTable";
import { getUserConfig, saveUserConfig } from "../../api/users";
import { USER_SHIPMENT_TABLE_COLUMNS } from "../../constants/ShipmentConstants";
import { useUserContext } from "../../context/UserContext";

const filtersExcludedForCount = ["reschedule", "isCustomerUser", "AppliedFiltersCount", "SearchQuery"];

const DashboardOverview = ({ accountInfo, onThemeChange, onLogout, setMetricsData }) => {
	const [appliedFilters, setAppliedFilters] = useState(null);
	const [exportPopupOpen, setExportPopupOpen] = useState(false);
	const [configColumnDailogOpen, setConfigColumnDailogOpen] = useState(false);
    // used for niagare users only.
    const [columns, setColumns] = useState(USER_SHIPMENT_TABLE_COLUMNS);
	const [rows, setRows] = useState([]);
	const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);
	const [isExportDisabled, setIsExportDisabled] = useState(false);
	const [totalShipmentCount, setTotalShipmentCount] = useState(0);
	const [open, setOpen] = React.useState(false); // state to toggle opening and closing of add customer popup
	const [isSaveBtnClicked, setIsSaveBtnClicked] = useState(false);
	const [isApplyBtnClicked, setIsApplyBtnClicked] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isSearchAction, setIsSearchAction] = useState(false);
    const [availableDates, setAvailableDates] = useState([]);
	const { userDetails } = useUserContext();

	const isCustomerUser = userDetails.isCustomerUser;
	const customerName = userDetails.customerName;

	const handleApplyFilters = (filters, isSearchAction = false) => {
		setIsApplyBtnClicked(true);
        const {SearchQuery, ...filtersObj} = {...appliedFilters, ...filters};
        setAppliedFilters(isSearchAction ? {...filtersObj, SearchQuery} : filtersObj);
        setIsSearchAction(isSearchAction);
	};

	const handleExportButtonClick = () => {
		setExportPopupOpen(true);
	};

    const onConfigColumnClicked = () => {
        setConfigColumnDailogOpen(true);
    };

    async function handleApplyColumns(list) {
        setConfigColumnDailogOpen(false);
        setColumns(list);
        const payload = {
            columns_config: list?.length ? list : [],
        }
        await saveUserConfig(payload);
    }

	const handleExportPopupClose = (format) => {
		setExportPopupOpen(false);
		if (format) {
			exportData(format);
		}
	};

	const formatDateTime = (dateTimeString) => {
		if (dateTimeString) {
			const formattedDateTime = moment(dateTimeString, "YYYYMMDDHHmmss").format("MM-DD-YY HH:mm");
			return formattedDateTime !== "Invalid date" ? formattedDateTime : "";
		}
		return "";
	};

	const handleRowsChange = (newRows) => {
		setRows(newRows);
	};

	const formatCellValueForCopy = (columnId, row) => {
		const formatTimestamp = (value) => {
			if (!value) return "";
			const formatted = moment(value, "YYYYMMDDHHmmss").format("MM/DD/YYYY HH:mm");
			return formatted !== "Invalid date" ? formatted : value;
		};

		switch (columnId) {
			case "DeliveryNumber":
				return row.DeliveryNumber || "";
			case "PONumber":
				return row.ShipmentDetails?.PONumber || "";
			case "DeliveryAppointment":
				return formatTimestamp(row.DeliveryAppointment);
			case "ShipDate":
				return formatTimestamp(row.ShipmentDetails?.ShipDate);
			case "DeliveryEta":
				return formatTimestamp(row.DeliveryEta);
			case "PickupAppointment":
				return formatTimestamp(row.ShipmentDetails?.PickupAppointment);
			case "RescheduleDate":
				return formatTimestamp(row.RescheduleDate);
			case "Customer":
				return row.CustomerName || "";
			case "CustomerRegion":
				return row.ShipmentDetails?.CustomerRegion || "";
			case "DeliveryStatus":
				return formatDeliveryStatus(row.DeliveryStatus) || "";
			case "Criticality":
				return row.ShipmentDetails?.Criticality || "";
			case "OrgName":
				return row.ShipmentDetails?.OrgName || "";
			case "Carrier":
				return row.ShipmentDetails?.Carrier || "";
			case "City":
				return row.City || "";
			case "State":
				return row.State || "";
			case "WareHouseNbr":
				return row.WareHouseNbr || row.ShipmentDetails?.WareHouseNbr || "";
			case "Comment":
				return row.Comment || "";
			case "Notes":
				return row.ShipmentDetails?.customerNotes || "";
			default:
				return row[columnId] ?? row.ShipmentDetails?.[columnId] ?? "";
		}
	};

	const customerColumns = [
		{ id: "WareHouseNbr", label: "Warehouse" },
		{ id: "City", label: "City" },
		{ id: "State", label: "State" },
		{ id: "PONumber", label: "PO Number" },
		{ id: "DeliveryAppointment", label: "Delivery Appointment" },
		{ id: "DeliveryEta", label: "Delivery ETA" },
		{ id: "DeliveryStatus", label: "Delivery Status" },
		{ id: "RescheduleDate", label: "Reschedule Date" },
		{ id: "Comment", label: "Comment" },
		{ id: "Notes", label: "Customer Notes" },
	];

	const handleCopyToClipboard = async () => {
		try {
			const response = await fetchShipments(
				appliedFilters,
				"DeliveryAppointment",
				"ascending",
				null,
				availableDates,
				isSearchAction,
				userDetails,
			);

			const allRows = response.shipments;
			if (!allRows || allRows.length === 0) {
				return false;
			}

			const visibleColumns = isCustomerUser
				? customerColumns
				: columns.filter(col => col.isSelected);
			const headers = visibleColumns.map(col => col.label).join("\t");
			const dataRows = allRows.map(row =>
				visibleColumns.map(col => {
					const value = formatCellValueForCopy(col.id, row);
					return String(value ?? "").replace(/[\t\n\r]/g, " ");
				}).join("\t")
			).join("\n");

			const tsvContent = `${headers}\n${dataRows}`;

			await navigator.clipboard.writeText(tsvContent);
			return true;
		} catch (err) {
			console.error("Failed to copy to clipboard:", err);
			return false;
		}
	};

	const handleFetchError = (hasError) => {
		setIsExportDisabled(hasError);
	};

	const formatOriginalRequestDate = (dateString) => {
		if (dateString) {
			let formattedDate;
			// Try parsing as "DD-MMM-YYYY"
			formattedDate = moment(dateString, "DD-MMM-YYYY", true);

			// If not valid, try parsing as "YYYY-MM-DD"
			if (!formattedDate.isValid()) {
				formattedDate = moment(dateString, "YYYY-MM-DD", true);
			}

			// If still not valid, return empty string
			if (!formattedDate.isValid()) {
				return "";
			}

			// Format the date as "MM-DD-YY"
			return formattedDate.format("MM-DD-YY");
		}
		return "";
	};

	const exportData = async (format) => {
		try {
			setIsExportDisabled(true); // Disable export button while fetching

			const response = await fetchShipments(
                appliedFilters,
                "DeliveryAppointment",
                "ascending",
                null,
                availableDates,
                isSearchAction,
				userDetails
            );

			const formattedData = response.shipments.map((row) => {
				if (isCustomerUser) {
					// Customer user specific fields
					return {
						"PO Number": row.ShipmentDetails.PONumber,
						Warehouse: row.WareHouseNbr,
						City: row.City,
						State: row.State,
						"Delivery Appointment": formatDateTime(row.DeliveryAppointment),
						"Delivery Status": formatDeliveryStatus(row.DeliveryStatus),
						"Delivery ETA": formatDateTime(row.DeliveryEta),
						"Customer Region": row.ShipmentDetails.CustomerRegion,
						Notes: row.Notes || row.ShipmentDetails?.Notes || "",
						Comment: row.Comment,
						"Reschedule Date": formatDateTime(row.ShipmentDetails?.RescheduleDate),
						Customer: row.CustomerName,
						"Delivery No.": row.DeliveryNumber,
                        "Customer Notes": row?.ShipmentDetails?.customerNotes || "",
					};
				} else {
					return {
						"Criticality": row.ShipmentDetails.Criticality,
						"Delivery No.": row.DeliveryNumber,
						"PO Number": row.ShipmentDetails.PONumber,
						"Delivery Appointment": formatDateTime(row.DeliveryAppointment),
						"Ship Date": formatDateTime(row.ShipmentDetails?.ShipDate),
						Source: row.ShipmentDetails.OrgName,
						City: row.City,
						"Delivery Status": formatDeliveryStatus(row.DeliveryStatus),
						Carrier: row.ShipmentDetails.Carrier,
						"NB Number": row.NBNumber,
						Preload: row.ShipmentDetails.Preload,
						"Transit Time": row.ShipmentDetails.TransitTime,
						Distance: roundToTwoDecimalPlaces(row.ShipmentDetails.Distance),
						"Pick-up ETA": formatDateTime(row.ShipmentDetails.PickupEta),
						"Pick-up Status": row.PickupStatus,
						State: row.State,
						"Pick-up Appointment": formatDateTime(row.ShipmentDetails.PickupAppointment),
						"Delivery ETA": formatDateTime(row.DeliveryEta),
						"Reason Code": row.ShipmentDetails.ReasonCode,
						"Reschedule Date": formatDateTime(row.ShipmentDetails.RescheduleDate),
						Tracker:
							row?.UpdatedBy || row.ShipmentDetails?.UpdatedBy || row.ShipmentDetails?.CreatedBy,
						"Last Update Time": convertUTCTimeToLocalTime(row.UpdatedTime),
						"Pick-up Time": formatDateTime(row.ShipmentDetails.PickupTime),
						Notes: row.Notes,
                        "Customer Notes": row?.ShipmentDetails?.customerNotes || "",
						Warehouse: row.WareHouseNbr,
						Comment: row.Comment,
						"214 Delivery Time": formatDateTime(row["214DeliveryTime"]) || "",
						"Four Kites Delivery Time": formatDateTime(row.FourkitesDeliveryTime) || "",
						"Customer Portal Delivery Time": formatDateTime(row.CustomerPortalDeliveryTime) || "",
						"Manual Delivery Time": formatDateTime(row.DeliveryTime) || "",
						"Portal Confirmed Delivery Time": formatDateTime(row.PortalConfirmedDeliveryTime) || "",
						Customer: row.CustomerName,
						"Transport Mode": row.ShipmentDetails.TransportMode,
						"Customer Region": row.ShipmentDetails.CustomerRegion,
						"Original Request Date": formatOriginalRequestDate(
							row.OriginalRequestDate || row.ShipmentDetails?.OriginalRequestDate,
						),
						"Customer ID": row.DestinationLocationGID,
						"Data Source": row.DataSource,
						"Phone Number": row.ContactDetails,
						Commodity: row.ShipmentDetails.Commodity,
					};
				}
			});
			if (format === "xlsx") {
				const worksheet = XLSX.utils.json_to_sheet(formattedData);
				// Adjust column widths
				const columnWidths = [
					{ wch: 20 }, // Delivery Number
					{ wch: 25 }, // PO Number
					{ wch: 20 }, // Delivery Appointment
					{ wch: 15 }, // Ship Date
					{ wch: 25 }, // Source
					{ wch: 20 }, // City
					{ wch: 20 }, // Delivery Status
					{ wch: 15 }, // Carrier
					{ wch: 15 }, // NB Number
					{ wch: 15 }, // Preload
					{ wch: 15 }, // Transit Time
					{ wch: 15 }, // Distance
					{ wch: 20 }, // Pickup ETA
					{ wch: 20 }, // Pickup Status
					{ wch: 10 }, // State
					{ wch: 20 }, // Pickup Appointment
					{ wch: 20 }, // Delivery ETA
					{ wch: 20 }, // Delivery Time
					{ wch: 25 }, // Reason Code
					{ wch: 20 }, // Tracker
					{ wch: 20 }, //Last Updated Time
					{ wch: 20 }, // Pickup Time
					{ wch: 25 }, //Notes
					{ wch: 15 }, // Warehouse
					{ wch: 25 }, // Customer Name
					{ wch: 20 }, // Transport Mode
					{ wch: 20 }, // Customer Region
					{ wch: 25 }, // Original Request Date
					{ wch: 15 }, // Customer ID
					{ wch: 15 }, // Data Source
					{ wch: 20 }, // Phone Number
					{ wch: 20 }, // Commodity
					{ wch: 15 }, // RescheduleDate
				];

				worksheet["!cols"] = columnWidths;

				const workbook = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(workbook, worksheet, "Shipments");
				const excelBuffer = XLSX.write(workbook, {
					bookType: "xlsx",
					type: "array",
				});
				const blob = new Blob([excelBuffer], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				});
				saveAs(blob, "shipments.xlsx");
			} else if (format === "csv") {
				const csvContent =
					"data:text/csv;charset=utf-8," +
					XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(formattedData));
				const encodedUri = encodeURI(csvContent);
				const link = document.createElement("a");
				link.setAttribute("href", encodedUri);
				link.setAttribute("download", "shipments.csv");
				document.body.appendChild(link);
				link.click();
			}
		} catch (error) {
			console.error("Error exporting data:", error);
		} finally {
			setIsExportDisabled(false); // Re-enable export button
		}
	};

    useEffect(() => {
        (async () => {
            const today = moment().format("YYYYMMDD");
    
            let initialFilters = {
                SelectedDate: [today],
            };

            if (accountInfo.account.userName) {
                const config = await getUserConfig();

                const filters = config?.filters ? config.filters : {};
                const columns_config = config?.columns_config ? config.columns_config : [];

                initialFilters = {
                    ...initialFilters,
                    ...filters
                };

                const colsFinal = columns_config;
                columns_config?.length && setColumns(colsFinal);
            }
            handleApplyFilters(initialFilters);
        })();
    }, [accountInfo.account.name, accountInfo.account.userName, customerName, isCustomerUser]);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDate(new Date());
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, []);

    useEffect(() => {
        let filtersCount = 0;
        if(!appliedFilters) {
            setAppliedFiltersCount(filtersCount);
            return;
        }
        filtersCount = Object.keys(appliedFilters)
            .filter(index => {
                if (filtersExcludedForCount.includes(index) || (isCustomerUser && index === "CustomerName")){
                    return false;
                } else if (appliedFilters[index]) {
                    return true;
                }
                return false;
            }).length;
        if (!appliedFilters.hasOwnProperty("SelectedDate")) {
            filtersCount += 1;
        }
        setAppliedFiltersCount(filtersCount);
    }, [appliedFilters, isCustomerUser]);

	// Fetch metrics when save or apply button is clicked
	useEffect(() => {
		const fetchMetricsData = async () => {
			try {
				const datesToUse =
					appliedFilters?.SelectedDate && appliedFilters.SelectedDate.length > 0
						? appliedFilters.SelectedDate
						: [moment().format("YYYYMMDD")];
				const data = await fetchMetrics(
					datesToUse,
					isCustomerUser ? '' : appliedFilters?.CustomerId || "",
					appliedFilters?.CustomerRegion || "",
					appliedFilters?.DeliveryStatus || "",
					appliedFilters?.City || "",
					appliedFilters?.Warehouse || "",
					appliedFilters?.reschedule || false,
					appliedFilters?.autoRecovery || false,
					userDetails
				);
				setMetricsData && setMetricsData(data);
				setIsSaveBtnClicked(false);
				setIsApplyBtnClicked(false);
			} catch (error) {
				console.error("Error fetching metrics:", error);
				setMetricsData && setMetricsData(null);
				setIsApplyBtnClicked(false);
			}
		};

		if (isSaveBtnClicked || isApplyBtnClicked) {
			fetchMetricsData();
		}
	}, [isSaveBtnClicked, isApplyBtnClicked]);

	return (
		<Box sx={{ padding: "0px", margin: "0px", height: "100%", display: "flex", flexDirection: "column" }}>
			<Box
				sx={{
					flexGrow: 1,
					display: "flex",
					flexDirection: "column",
					padding: "0px",
					paddingBottom: 0,
					overflow: "hidden",
				}}
			>
				<Search
					onApplyFilters={handleApplyFilters}
					onExportButtonClick={handleExportButtonClick}
					onCopyToClipboard={handleCopyToClipboard}
					isCopyDisabled={rows.length === 0}
					shipmentCount={rows.length}
                    onConfigColClicked={onConfigColumnClicked}
                    configColumnDailogOpen={configColumnDailogOpen}
                    setIsConfigColumnDailogOpen={setConfigColumnDailogOpen}
                    columns={columns}
                    handleApplyColumns={handleApplyColumns}
					exportPopupOpen={exportPopupOpen}
					onExportPopupClose={handleExportPopupClose}
					appliedFiltersCount={appliedFiltersCount}
					appliedFilters={appliedFilters}
					isExportDisabled={isExportDisabled}
					accountInfo={accountInfo}
                    onAvailableDatesChanged={setAvailableDates}
				/>
				{isCustomerUser ? (
					<CustomerShipmentTable
                        isSearchAction={isSearchAction}
						appliedFilters={appliedFilters}
						onRowsChange={handleRowsChange}
						onTotalCountChange={setTotalShipmentCount}
						accountInfo={accountInfo}
						onFetchError={handleFetchError}
                        availableDates={availableDates}
					/>
				) : (
					<ShipmentTable
                        columns={columns.filter(k => k.isSelected)}
                        isSearchAction={isSearchAction}
						appliedFilters={appliedFilters}
						onRowsChange={handleRowsChange}
						onTotalCountChange={setTotalShipmentCount}
						accountInfo={accountInfo}
						onFetchError={handleFetchError}
						setIsSaveBtnClicked={setIsSaveBtnClicked}
                        availableDates={availableDates}
					/>
				)}

			</Box>
			<AddCustomerPopup open={open} setOpen={setOpen} />
		</Box>
	);
};

export default DashboardOverview;
